import * as React from 'react'
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useOverlayScrollbars } from 'overlayscrollbars-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import _ from 'lodash';
import moment from 'moment-timezone';

import { DropdownTableFilter } from '../../components/DropdownTableFilter';
import { PAGE_LIMIT } from '../../constants';
import { tableActions } from './reducer';
import { CHECKBOX_COLUMN_ID, CheckboxColumn } from './components/CheckboxColumn';
import { EXPANSION_COLUMN_ID, getExpansionColumn } from './components/ExpansionColumn';
import Select from '../../components/Select';
import Strings from '../../Strings';

export const columnHelper = createColumnHelper();
const ACTIONS_COLUMN_ID = 'table-actions-column';
export const PAGES_BATCH = 2;
const ENABLE_COLUMN_RESIZING = true;
const SORT_COLUMN_PARAM = 'sortColumn';
const SORT_TYPE_PARAM = 'sortType';

const getTableKey = name => `${name}__20240120`;

const getColumnWidth = (header, columnSizing) => {
  // Use user-resized width, then columnDef.size, then fall back to getSize()
  return !isNaN(columnSizing[header.id]) ? columnSizing[header.id]
    : !isNaN(header.column?.columnDef?.size) ? header.column.columnDef.size
    : header.getSize();
}

const Table = React.forwardRef((props, ref) => {
  const {
    name: tableName,
    data,
    columns,
    enableColumnFiltering = true,
    pinFirstColumn,
    pagination,
    sorting,
    isLoading,
    onOffsetChange,
    onSortingChange,
    onPageSizeChange,
    onRowClick,
    enableRowExpansion,
    renderExpandedRow,
    buttons,
    tableState,
    setColumnsVisibility,
    setColumnsSizing,
    setColumnPinning,
    setColumnsOrder,
    setGlobalPageSize,
    onRowSelectionChange,
    footerExtra,
    noPagination = false,
    getRowClassName,
  } = props;

  const name = React.useMemo(() => getTableKey(tableName), [tableName]);
  const enableRowSelection = !!onRowSelectionChange;
  const shouldEnableRowExpansion = !!enableRowExpansion && !!renderExpandedRow;
  const superPinnedColumns = React.useMemo(() => (enableRowSelection ? [CHECKBOX_COLUMN_ID] : [])
    .concat(shouldEnableRowExpansion ? [EXPANSION_COLUMN_ID] : []), [enableRowSelection, shouldEnableRowExpansion]);
  const getColumnOrderWithNewColumns = React.useCallback((allColumns) => {
    const allColumnKeys = allColumns.map(c => c.accessorKey);
    // Keep existing order for columns that are still present, append new columns at the end
    return [...tableState?.columnOrder?.filter(col => allColumnKeys.includes(col)) 
      || [], ...allColumnKeys.filter(col => !(tableState?.columnOrder || []).includes(col))];
  }, [tableState?.columnOrder]);

  // Controlled states - must be declared before table configuration
  const [paginationState, setPaginationState] = React.useState({
    pageIndex: pagination ? Math.floor((pagination.offset || 0) / (pagination.limit || PAGE_LIMIT)) : 0,
    pageSize: pagination?.limit || tableState?.globalPageSize || PAGE_LIMIT,
  });
	const [columnVisibility, setColumnVisibilityState] = React.useState(tableState?.columnVisibility || {});
	const [columnSizing, setColumnSizingState] = React.useState(tableState?.columnSizing || {});
  const [columnPinning, setColumnPinningState] = React.useState(
    tableState?.columnPinning || {
      left: superPinnedColumns
        .concat(pinFirstColumn ? [columns[0]?.accessorKey] : [])
    }
  );
  const [columnOrder, setColumnOrderState] = React.useState(tableState?.columnOrder ? getColumnOrderWithNewColumns(columns) : columns.map(c => c.accessorKey).filter(Boolean));
  const [sortingState, setSortingState] = React.useState(() => {
    const params = new URLSearchParams(window.location.search);
    const sortColumn = params.get(SORT_COLUMN_PARAM);
    const sortType = params.get(SORT_TYPE_PARAM);

    if (sortColumn) {
      return [{
        id: sortColumn,
        desc: String(sortType || '').toLowerCase() === 'desc'
          || String(sortType || '').toLowerCase() === 'descending',
      }];
    }

    return tableState?.sorting || sorting || [];
  });
  const sortingStateRef = React.useRef(sortingState);
  React.useEffect(() => {
    sortingStateRef.current = sortingState;
  }, [sortingState]);
  const [rowSelection, setRowSelection] = React.useState({});
  const [expandedRowIds, setExpandedRowIds] = React.useState(new Set());

  const isRowExpanded = React.useCallback(rowId => expandedRowIds.has(rowId), [expandedRowIds]);

  const toggleRowExpansion = React.useCallback(rowId => {
    setExpandedRowIds(current => {
      const next = new Set(current);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  const getCurrentHistoryState = React.useCallback(() => {
    const state = window.history.state;
    return state && typeof state === 'object' ? state : {};
  }, []);

  const getStoredScrollPosition = React.useCallback(() => {
    const storedValue = getCurrentHistoryState()?.tableScrollPositions?.[name];
    const parsed = Number.parseInt(storedValue, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : NaN;
  }, [getCurrentHistoryState, name]);

  const saveScrollPositionToHistoryState = React.useCallback(scrollTop => {
    const currentState = getCurrentHistoryState();
    const tableScrollPositions = {
      ...(currentState.tableScrollPositions || {}),
    };

    if (scrollTop > 0) {
      tableScrollPositions[name] = Math.round(scrollTop);
    } else {
      delete tableScrollPositions[name];
    }

    const nextState = {
      ...currentState,
    };

    if (Object.keys(tableScrollPositions).length > 0) {
      nextState.tableScrollPositions = tableScrollPositions;
    } else {
      delete nextState.tableScrollPositions;
    }

    window.history.replaceState(
      nextState,
      '',
      `${window.location.pathname}${window.location.search}${window.location.hash}`
    );
  }, [getCurrentHistoryState, name]);

  const savePaginationToUrlParams = React.useCallback((newPagination, newSorting = sortingStateRef.current) => {
    const params = new URLSearchParams(window.location.search);

    if (newPagination.pageIndex > 0) {
        params.set('page', newPagination.pageIndex);
    } else {
        params.delete('page');
    }
    if (newPagination.pageSize && newPagination.pageSize !== PAGE_LIMIT) {
        params.set('pageSize', newPagination.pageSize);
    } else {
        params.delete('pageSize');
    }

    const primarySort = Array.isArray(newSorting) ? newSorting[0] : undefined;
    if (primarySort?.id) {
      params.set(SORT_COLUMN_PARAM, primarySort.id);
      params.set(SORT_TYPE_PARAM, primarySort.desc ? 'descending' : 'ascending');
    } else {
      params.delete(SORT_COLUMN_PARAM);
      params.delete(SORT_TYPE_PARAM);
    }

    const query = params.toString();
    const nextUrl = query
      ? `${window.location.pathname}?${query}${window.location.hash}`
      : `${window.location.pathname}${window.location.hash}`;
    window.history.replaceState(getCurrentHistoryState(), '', nextUrl);
  }, [getCurrentHistoryState]);

  // Custom pagination handler to convert between offset/limit and pageIndex/pageSize
  const handlePaginationChange = React.useCallback((updaterOrValue) => {
    let newPaginationState;

    if (typeof updaterOrValue === 'function') {
      newPaginationState = updaterOrValue(paginationState);
    } else {
      newPaginationState = updaterOrValue;
    }
    savePaginationToUrlParams(newPaginationState, sortingStateRef.current);
    setPaginationState(newPaginationState);
  }, [paginationState, savePaginationToUrlParams]);

  React.useEffect(() => {
    if (pagination) {
      const newPageIndex = Math.floor((pagination.offset || 0) / (pagination.limit || PAGE_LIMIT));
      const newPageSize = pagination.limit || PAGE_LIMIT;

      setPaginationState(prevState => {
        // Only update if values actually changed to avoid unnecessary re-renders
        if (prevState.pageIndex !== newPageIndex || prevState.pageSize !== newPageSize) {
          return {
            pageIndex: newPageIndex,
            pageSize: newPageSize,
          };
        }
        return prevState;
      });
    }
  }, [pagination?.offset, pagination?.limit]);

  const hasInitializedSortingEffectRef = React.useRef(false);

  // Add buttons column if buttons are provided; add checkbox column if row selection is enabled
  const tableColumns = React.useMemo(() => {
    const dataColumns = columns;
    const expansionColumn = shouldEnableRowExpansion
      ? [getExpansionColumn(isRowExpanded, toggleRowExpansion)]
      : [];
    return (enableRowSelection ? [CheckboxColumn] : [])
      .concat(expansionColumn)
      .concat(dataColumns)
      .concat(buttons?.length ? [
      columnHelper.display({
        id: ACTIONS_COLUMN_ID,
        cell: p => <TableRowActions rowId={p.row.id} data={p.row.original} buttons={buttons} />,
        size: 0,
        minSize: 0,
      })] : []);
  }, [columns, buttons, enableRowSelection, shouldEnableRowExpansion, isRowExpanded, toggleRowExpansion]);


  const table = useReactTable({
    data: data || [],
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    // Controlled states
    state: {
      pagination: paginationState,
      columnVisibility,
      columnSizing,
      columnPinning,
      columnOrder,
      rowSelection,
      sorting: sortingState,
      pageSize: tableState?.pageSize,
    },
    onPaginationChange: handlePaginationChange,
    onColumnVisibilityChange: setColumnVisibilityState,
    onColumnSizingChange: setColumnSizingState,
    onColumnPinningChange: setColumnPinningState,
    onColumnOrderChange: setColumnOrderState,
    onSortingChange: setSortingState,
    onRowSelectionChange: setRowSelection,
    // Without pagination every row is shown (no pager, so a paged row model would hide all but
    // the first page).
    getPaginationRowModel: noPagination ? undefined : getPaginationRowModel(),
    manualPagination: !!pagination,
    manualSorting: !!pagination,
    rowCount: pagination ? (pagination.totalRecords ?? 0) : (data?.length || undefined),
    columnResizeMode: 'onChange',
    enableColumnResizing: ENABLE_COLUMN_RESIZING,
    enableSorting: onSortingChange || !pagination,
    getSortedRowModel: !pagination ? getSortedRowModel() : undefined,
    sortingFns: {
      dateSortingFn: (rowA, rowB, columnId) => {
        const sortingOrder = table.getColumn(columnId).getIsSorted();
        const valueA = rowA.getValue(columnId);
        const valueB = rowB.getValue(columnId);
        const momentA = valueA ? moment(valueA) : null;
        const momentB = valueB ? moment(valueB) : null;
        if (!momentA && !momentB) return 0;
        if (!momentA) return sortingOrder === 'desc' ? -1 : 1;
        if (!momentB) return sortingOrder === 'desc' ? 1 : -1;
        return momentA.unix() - momentB.unix();
      },
    },
  });

  React.useImperativeHandle(
    ref,
    () => ({
      resetSelectedRows: () => table.toggleAllRowsSelected(false),
    }),
    []
  )

  const { rows } = table.getRowModel();
  const scrollRef = React.useRef();
  const viewportRef = React.useRef();
  const tableContainerRef = React.useRef();
  const restoreScrollFrameRef = React.useRef(null);
  const hasRestoredScrollRef = React.useRef(false);
  const isRestoringScrollRef = React.useRef(false);

  // Track previous row count and column widths for loading state
  React.useEffect(() => {
    if (rows.length > 0) {
      // Store current column widths when we have data (including user-resized widths)
      const headers = table.getFlatHeaders();
      const widths = {};
      headers.forEach(header => {
        widths[header.id] = getColumnWidth(header, columnSizing);
      });
    }
  }, [rows.length, table, columnSizing]);

  const restoreScrollFromHistoryState = React.useCallback((instance) => {
    if (hasRestoredScrollRef.current) {
      return;
    }

    if (isRestoringScrollRef.current) {
      return;
    }

    const scrollTo = getStoredScrollPosition();

    if (isNaN(scrollTo) || scrollTo < 0) {
      hasRestoredScrollRef.current = true;
      return;
    }

    const MAX_ATTEMPTS = 300;
    const MIN_STABLE_HEIGHT_FRAMES = 2;
    isRestoringScrollRef.current = true;
    let attempts = 0;
    let previousHeight = 0;
    let stableHeightFrames = 0;

    const tryRestore = () => {
      const viewport = instance?.elements()?.viewport;

      if (!viewport) {
        isRestoringScrollRef.current = false;
        restoreScrollFrameRef.current = null;
        return;
      }

      attempts += 1;
      const clientHeight = viewport.clientHeight;
      const maxScrollTop = Math.max(0, viewport.scrollHeight - clientHeight);

      if (clientHeight > 0 && clientHeight === previousHeight) {
        stableHeightFrames += 1;
      } else {
        stableHeightFrames = 0;
      }
      previousHeight = clientHeight;

      const shouldApplyScroll = maxScrollTop >= scrollTo
        || (stableHeightFrames >= MIN_STABLE_HEIGHT_FRAMES && maxScrollTop > 0)
        || (attempts >= MAX_ATTEMPTS && maxScrollTop > 0);

      if (shouldApplyScroll) {
        viewport.scrollTo({ top: Math.min(scrollTo, maxScrollTop), behavior: 'smooth' });
        hasRestoredScrollRef.current = true;
        isRestoringScrollRef.current = false;
        restoreScrollFrameRef.current = null;
        return;
      }

      if (attempts >= MAX_ATTEMPTS) {
        // Content is likely still not rendered enough to be scrollable.
        // Leave hasRestoredScrollRef as false so the `updated` event can retry.
        isRestoringScrollRef.current = false;
        restoreScrollFrameRef.current = null;
        return;
      }

      restoreScrollFrameRef.current = window.requestAnimationFrame(tryRestore);
    };

    restoreScrollFrameRef.current = window.requestAnimationFrame(tryRestore);
  }, [getStoredScrollPosition]);

  const [initialize, osInstance] = useOverlayScrollbars({
    defer: true,
    options: { scrollbars: { autoHide: 'leave', autoHideDelay: '100' } },
    events: {
      scroll: _.debounce(() => {
        const viewport = osInstance()?.elements()?.viewport;
        if (!viewport) {
          return;
        }
        const scrollTop = viewport?.scrollTop ?? 0;
        saveScrollPositionToHistoryState(scrollTop);
      }, 100),
      initialized: (instance) => {
        restoreScrollFromHistoryState(instance);
      },
      updated: (instance) => {
        restoreScrollFromHistoryState(instance);
      }
    },
  });

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('scroll')) {
      return;
    }

    params.delete('scroll');
    const query = params.toString();
    const nextUrl = query
      ? `${window.location.pathname}?${query}${window.location.hash}`
      : `${window.location.pathname}${window.location.hash}`;

    window.history.replaceState(getCurrentHistoryState(), '', nextUrl);
  }, [getCurrentHistoryState]);

  React.useEffect(() => {
    if (scrollRef.current && viewportRef.current) {
      initialize({ target: scrollRef.current, elements: { viewport: viewportRef.current } });
    }

    return () => {
      if (restoreScrollFrameRef.current) {
        window.cancelAnimationFrame(restoreScrollFrameRef.current);
        restoreScrollFrameRef.current = null;
      }
      isRestoringScrollRef.current = false;
      osInstance()?.destroy();
    }
  }, [initialize, osInstance]);

  React.useEffect(() => {
    if (!hasInitializedSortingEffectRef.current) {
      hasInitializedSortingEffectRef.current = true;
      if (onSortingChange) {
        onSortingChange(sortingState);
      }
      return;
    }

    const nextPaginationState = {
      ...paginationState,
      pageIndex: 0,
    };

    savePaginationToUrlParams(nextPaginationState, sortingStateRef.current);

    if (paginationState.pageIndex !== 0) {
      setPaginationState(nextPaginationState);
    }

    const viewport = osInstance()?.elements()?.viewport;
    if (viewport?.scrollTop > 0) {
      viewport.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (onSortingChange) {
      onSortingChange(sortingState);
    }
  }, [savePaginationToUrlParams, sortingState, onSortingChange]);


  const onPaginationManuallyChanged = value => {
    if (onOffsetChange) {
      onOffsetChange(value * paginationState.pageSize);
    }
    osInstance()?.elements().viewport.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = newSize => {
    // Save globally in Redux store for persistence across all tables
    setGlobalPageSize(newSize);
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  React.useEffect(() => {
    setColumnsVisibility(name, columnVisibility);
  }, [name, setColumnsVisibility, columnVisibility]);

  React.useEffect(() => {
    setColumnsSizing(name, columnSizing);
  }, [name, setColumnsSizing, columnSizing]);

  React.useEffect(() => {
    setColumnsOrder(name, columnOrder);
  }, [name, setColumnOrderState, columnOrder]);

  React.useEffect(() => {
    // Initialize global page size in Redux if not already set
    if (!tableState?.globalPageSize && paginationState.pageSize !== PAGE_LIMIT) {
      setGlobalPageSize(paginationState.pageSize);
    }
  }, [setGlobalPageSize, tableState?.globalPageSize, paginationState.pageSize]);

  // Sync pagination state with global page size from Redux when it changes
  React.useEffect(() => {
    if (!pagination && tableState?.globalPageSize && tableState.globalPageSize !== paginationState.pageSize) {
      setPaginationState(prev => ({
        ...prev,
        pageSize: tableState.globalPageSize,
      }));
    }
  }, [pagination, tableState?.globalPageSize]);

  React.useEffect(() => {
    if (!onRowSelectionChange) {
      return;
    }
    const selectedRows = Object.keys(rowSelection).map(index => data[parseInt(index, 10)]).filter(Boolean);
    onRowSelectionChange(selectedRows);
  }, [onRowSelectionChange, rowSelection, data]);

  React.useEffect(() => {
    setExpandedRowIds(new Set());
  }, [data]);

  const filters = React.useMemo(() => {
    return table.getAllLeafColumns()
    .filter(column => column.columnDef.id !== CHECKBOX_COLUMN_ID
      && column.columnDef.id !== EXPANSION_COLUMN_ID
      && column.columnDef.id !== ACTIONS_COLUMN_ID)
    .map(column => {
      return {
        // A header rendered by a function names its column in meta.label.
        label: typeof column.columnDef.header === 'function'
          ? (column.columnDef.meta?.label || column.id)
          : column.columnDef.header,
        id: column.id,
        checked: column.getIsVisible(),
        disabled: column.columnDef.enableHiding === false,
      };
    });
  }, [columnVisibility, columnPinning, columnOrder, columns]);

  const onSetColumns = (id, checked) => {
    table.getAllFlatColumns().find(c => c.id === id).toggleVisibility(checked);
  };

  const resetColumnsFilters = () => {
    setColumnVisibilityState({});
    setColumnSizingState({});
    setColumnPinningState({left: superPinnedColumns
      .concat(pinFirstColumn ? [columns[0]?.accessorKey] : [])});
    setColumnOrderState(columns.map(c => c.accessorKey));
  };

  const SUPER_PINNED_COLUMNS = superPinnedColumns;

  React.useEffect(() => {
    setColumnPinningState(current => {
      const currentLeft = current?.left || [];
      const nonSuperPinned = currentLeft.filter(id => !SUPER_PINNED_COLUMNS.includes(id)
        && id !== CHECKBOX_COLUMN_ID
        && id !== EXPANSION_COLUMN_ID);
      return {
        ...current,
        left: SUPER_PINNED_COLUMNS.concat(nonSuperPinned),
      };
    });
  }, [SUPER_PINNED_COLUMNS]);
  const changeColumnOrderIndexes = (sourceIndex, targetIndex, skipPinningCheck = false) => {
    const sourceColumn = columnOrder[sourceIndex];
    const newColumnOrder = [...columnOrder];
    newColumnOrder.splice(targetIndex, 0, newColumnOrder.splice(sourceIndex, 1)[0]);
    setColumnOrderState(newColumnOrder);

    if (skipPinningCheck) {
      return;
    }
    // Pin the column if it's being moved to the left beyond last pinned column
    const wasPinned = columnPinning.left?.includes(sourceColumn);
    const leftLength = columnPinning?.left?.length ?? 0;
    if (targetIndex + SUPER_PINNED_COLUMNS.length < leftLength) {
      setColumnPinningState(pinning => ({
        ...pinning,
        left: SUPER_PINNED_COLUMNS.concat(newColumnOrder.slice(0, leftLength + (wasPinned ? -1 : 0))),
      }));

    }

    // Unpin the column if it's being moved to the right beyond last pinned column
    if (targetIndex > leftLength - SUPER_PINNED_COLUMNS.length - 1) {
      if (!wasPinned) {
        return;
      }
      setColumnPinningState(pinning => ({
        ...pinning,
        left: pinning.left.filter(id => id !== sourceColumn),
      }));
    }

  };

  const changeColumnPinning = (column, direction) => {
    const isPinned = columnPinning?.left?.includes(column);
    const leftLength = columnPinning?.left?.length ?? 0;
    const superPinnedCount = SUPER_PINNED_COLUMNS?.length ?? 0;
    const newIndex = isPinned ? leftLength - superPinnedCount - 1 : leftLength - superPinnedCount;
    setColumnPinningState(pinning => {
      if (isPinned && direction === false) {
        return {
          ...pinning,
          left: pinning.left.filter(id => id !== column),
        };
      }
      if (!isPinned && direction === 'left') {
        return {
          ...pinning,
          left: [...(pinning.left || []), column],
        };
      }
      return pinning;
    });
    // Change column order to match pinning
    changeColumnOrderIndexes(columnOrder.indexOf(column), newIndex, true);
  };
  React.useEffect(() => {
    setColumnPinning(name, columnPinning);
  }, [name, columnPinning, setColumnPinning]);

  const changeColumnOrder = (sourceColumnId, targetColumnId) => {
    changeColumnOrderIndexes(columnOrder.indexOf(targetColumnId), columnOrder.indexOf(sourceColumnId));
  };

  const moveColumnUp = (columnId) => {
    const currentIndex = columnOrder.indexOf(columnId);
    if (currentIndex > 0) {
      changeColumnOrder(columnOrder[currentIndex - 1], columnId);
    }
  };

  const moveColumnDown = (columnId) => {
    const currentIndex = columnOrder.indexOf(columnId);
    if (currentIndex < columnOrder.length - 1) {
      changeColumnOrder(columnId, columnOrder[currentIndex + 1]);
    }
  };

  const [containerWidth, setContainerWidth] = React.useState(0);

  // Track container width for responsive column sizing
  React.useEffect(() => {
    if (!tableContainerRef.current) return;

    const updateWidth = () => {
      if (tableContainerRef.current) {
        setContainerWidth(tableContainerRef.current.clientWidth);
      }
    };

    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(tableContainerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const columnSizeVars = React.useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes = {};
    let totalWidth = 0;

    for (let i = 0; i < headers.length; i += 1) {
      const header = headers[i];

      // Use user-resized width if available, otherwise use columnDef.size
      const size = getColumnWidth(header, columnSizing);
      colSizes[`--header-${header.id}-size`] = size;
      colSizes[`--col-${header.column.id}-size`] = size;
      totalWidth += size;
    }
    colSizes['--table-total-width'] = totalWidth;

    return colSizes;
  }, [columnSizing, columns]);

  return (
    <div className="table-container" style={{minHeight: 1}} ref={tableContainerRef}>
      {enableColumnFiltering && (
        <DropdownTableFilter
          containerWidth={tableContainerRef.current?.clientWidth && (columnSizeVars['--table-total-width'] > tableContainerRef.current?.clientWidth) ? tableContainerRef.current?.clientWidth : columnSizeVars['--table-total-width']}
          filters={filters}
          handleChange={onSetColumns}
          resetToDefaults={resetColumnsFilters}
          dontDisableFirstEntry
          advancedFeatures={{ changeColumnPinning, pinningState: columnPinning, onUp: moveColumnUp, onDown: moveColumnDown, changeColumnOrder: changeColumnOrderIndexes }}
        />
      )}
      <div
        className="simplebar-100percent-width"
        ref={scrollRef}
        style={{
          ...columnSizeVars,
           width: table.getTotalSize() + 2,
        }}
      >
        <div ref={viewportRef}>
          <div
            style={{
               width: table.getTotalSize(),
              position: 'relative',
            }}
          >
            <table className={`table fixed-layout ${name}`} aria-busy={Boolean(isLoading)} style={{ width: `${columnSizeVars['--table-total-width']}px`, minWidth: `${columnSizeVars['--table-total-width']}px` }}>
              <colgroup>
                {table.getFlatHeaders().map(header => {
                  const width = columnSizeVars[`--col-${header.column.id}-size`] || getColumnWidth(header, columnSizing);
                  return <col key={header.id} style={{ width: `${width}px`, minWidth: `${width}px` }} />;
                })}
              </colgroup>
              <thead className="table-header">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id} className={`table-row ${buttons?.length ? 'with-buttons' : ''}`}>
                    {headerGroup.headers.map(header => {
                      const { column } = header;
                      // Actions column header is not rendered
                      if (column.id === 'actions') {
                        return;
                      }
                    return (
                      <th key={header.id} className={getColumnClassName(column)} style={{...getCommonPinningStyles(column, columnSizing)}}>
                        <span
                          className={`header-cell ${column.getCanSort() ? 'sortable-header' : ''}`}
                          onClick={column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                          data-testid={`header[${headerGroup.id}][${header.id}]-header`}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                          )}
                          {column.getCanSort() && <span className={`sort-icon ${column.getIsSorted() || ''}`} data-testid={`header[${headerGroup.id}][${header.id}]-sort`} />}
                        </span>
                        {ENABLE_COLUMN_RESIZING && (header.column.columnDef?.enableResizing !== false) && (
                          <div
                            {...{
                              onDoubleClick: () => header.column.resetSize(),
                              onMouseDown: header.getResizeHandler(),
                              onTouchStart: header.getResizeHandler(),
                              className: `resizer ${
                                header.column.getIsResizing() ? 'isResizing' : ''
                              }`,
                            }}
                          />
                        )}
                      </th>
                    )}
                  )}
                  </tr>
                ))}
              </thead>
              {table.getState().columnSizingInfo.isResizingColumn ? (
                <MemoizedTableBody
                  table={table}
                  isLoading={isLoading}
                  onRowClick={onRowClick}
                  isRowExpanded={isRowExpanded}
                  shouldEnableRowExpansion={shouldEnableRowExpansion}
                  renderExpandedRow={renderExpandedRow}
                  buttons={buttons}
                  getRowClassName={getRowClassName}
                  columnSizing={columnSizing}
                  containerWidth={containerWidth}
                />
              ) : (
                <TableBody
                  table={table}
                  isLoading={isLoading}
                  onRowClick={onRowClick}
                  isRowExpanded={isRowExpanded}
                  shouldEnableRowExpansion={shouldEnableRowExpansion}
                  renderExpandedRow={renderExpandedRow}
                  buttons={buttons}
                  getRowClassName={getRowClassName}
                  columnSizing={columnSizing}
                  containerWidth={containerWidth}
                />
              )}

              {/* <tfoot>
                {table.getFooterGroups().map(footerGroup => (
                  <tr key={footerGroup.id}>
                    {footerGroup.headers.map(header => (
                      <th key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.footer,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </tfoot> */}
            </table>
          </div>
        </div>
      </div>
      {((rows?.length > 0 || paginationState.pageIndex > 0 || pagination?.totalRecords > 0) && !noPagination) && (
        <Pagination
          paginationState={paginationState}
          table={table}
          isLoading={isLoading}
          onPaginationManuallyChanged={onPaginationManuallyChanged}
          onPageSizeChange={handlePageSizeChange}
          footerExtra={footerExtra}
        />
      )}
    </div>
  )
});

const mapStateToProps = (state, props) => {
  const { name, columns } = props;
  return {
    tableState: {
      ...(state.tableReducer[getTableKey(name, columns)] || {}),
      globalPageSize: state.tableReducer.globalPageSize,
    },
  };
};

const mapDispatchToProps = {
  setColumnsVisibility: tableActions.setColumnsVisibility,
  setColumnsSizing: tableActions.setColumnsSizing,
  setColumnPinning: tableActions.setColumnPinning,
  setColumnsOrder: tableActions.setColumnsOrder,
  setGlobalPageSize: tableActions.setGlobalPageSize,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(Table);

const getCommonPinningStyles = (column, columnSizing = {}) => {
  const isPinned = column.getIsPinned();
  const size = getColumnWidth(column, columnSizing);
  return {
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    width: size ? `${size}px` : undefined,
    minWidth: size ? `${size}px` : undefined,
  };
}

const getColumnClassName = column => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');
  const isActionColumn = column.id === ACTIONS_COLUMN_ID;
  const isCheckboxColumn = column.id === CHECKBOX_COLUMN_ID;
  const isExpansionColumn = column.id === EXPANSION_COLUMN_ID;
  const isSortable = column.getCanSort();
  const sortOrder = column.getIsSorted();

  return [
    isPinned ? "pinned" : null,
    isLastLeftPinnedColumn ? 'last-left-pinned' : null,
    isFirstRightPinnedColumn ? 'first-right-pinned' : null,
    isActionColumn ? 'sticky-right' : null,
    isCheckboxColumn ? 'checkbox-column' : null,
    isExpansionColumn ? 'expansion-column' : null,
    isSortable ? 'sortable' : null,
    sortOrder || null,
  ]
    .filter(Boolean)
    .join(' ')
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function Pagination({ paginationState, table, isLoading, onPaginationManuallyChanged, onPageSizeChange, footerExtra }) {
  const paginationInputRef = React.useRef();
  const currentPage = paginationState.pageIndex;
  const pageSize = paginationState.pageSize;

  const onPageIndexChange = _.debounce((pageIndex) => {
    paginationInputRef?.current?.blur();
    paginationInputRef.current.value = null;
    table.setPageIndex(pageIndex);
    if (onPaginationManuallyChanged) {
      onPaginationManuallyChanged(pageIndex);
    }
  }, 1000);

  const onPageIndexChangeNoDebounce = pageIndex => {
    table.setPageIndex(pageIndex);
    if (onPaginationManuallyChanged) {
      onPaginationManuallyChanged(pageIndex);
    }
  };

  const handlePageSizeChange = option => {
    const newSize = Number(option.value);
    table.setPageSize(newSize);
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  return (
    <div className="table-pagination" style={{ width: table.getTotalSize(), maxWidth: '100%' }}>
      <div className="page-size-selector">
        <Select
          id="table-page-size-select"
          value={pageSize}
          onChange={handlePageSizeChange}
          data={PAGE_SIZE_OPTIONS.map(size => ({value: size, label: size}))}
        />
        <span>per page</span>
      </div>
      {footerExtra}
      <div className={`footerButtonContainer ${!table.getCanPreviousPage() ? 'disabled' : ''}`}>
        <button
          className={`add-arrow left prev ${!table.getCanPreviousPage() ? 'disabled' : ''}`}
          onClick={() => onPageIndexChangeNoDebounce(currentPage - 1)}
          data-testid="table-prev-page"
        />
      </div>
      {isLoading? (
        <div className="pagination-spinner-container">
          <div className="spinner" />
        </div>
      ) : (
        <input
          ref={paginationInputRef}
          type="text"
          placeholder={`${currentPage + 1} / ${table.getPageCount().toLocaleString()}`}
          data-testid="table-custom-page"
          onChange={e => {
            const page = e.target.value ? Number(e.target.value) - 1 : 0
            onPageIndexChange(page)
          }}
        />
      )}
      <div className={`footerButtonContainer ${!table.getCanNextPage() ? 'disabled' : ''}`}>
        <button
          className={`add-arrow right next ${!table.getCanNextPage() ? 'disabled' : ''}`}
          onClick={() => onPageIndexChangeNoDebounce(currentPage + 1)}
          data-testid="table-next-page"
        />
      </div>
    </div>
  );
};

export function TableRowActions ({buttons, rowId, data}) {
  return buttons && buttons.length > 0 ? (
    <div className="buttons-wrapper">
      <div className="buttons-background-gradient" />
      <div className="buttons-background">
        <div className="buttons">
          {buttons.map(b => {
            const visible = typeof b.visible === 'function'
              ? b.visible(rowId, data)
              : (typeof b.visible === 'boolean' ? b.visible : true);
            if (!visible) {
              return null;
            }
            const icon = typeof b.icon === 'function' ? b.icon(rowId, data) : b.icon;
            const testId = typeof b.testId === 'function' ? b.testId(rowId, data) : (b.testId || icon);
            const disabled = (typeof b.disabled === 'function' && b.disabled(rowId, data)) || (typeof b.disabled == 'boolean' && b.disabled);
            const highlighted = (typeof b.highlighted === 'function' && b.highlighted(rowId, data) === true) || (typeof b.highlighted == 'boolean' && b.highlighted);
            const text  = typeof b.text === 'function' ? b.text(rowId, data) : b.text;
            const disabledTooltip = typeof b.disabledTooltip === 'function' ? b.disabledTooltip(rowId, data) : b.disabledTooltip;
            return (
              <div
                key={`button_${b.text}_${rowId}`}
                className="icon-container"
                data-tooltip-content={disabled && disabledTooltip ? disabledTooltip : text}
                data-tooltip-id="tooltip"
              >
                <div
                  className={`icon ${icon} ${disabled ? 'disabled' : ''} ${highlighted ? 'highlighted': ''} `}
                  onClick={e => {
                    e.stopPropagation();
                    b.onClick(rowId, data);
                  }}
                  data-testid={`table-button_${testId}_${rowId}`}
                />
              </div>
            )})}
        </div>
      </div>
    </div>
) : <></>};

TableRowActions.propTypes = {
  buttons: PropTypes.arrayOf(PropTypes.object),
  rowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  data: PropTypes.any,
};

function TableBody({ table, isLoading, onRowClick, isRowExpanded, shouldEnableRowExpansion, renderExpandedRow, buttons, columnSizing, containerWidth, getRowClassName }) {
  const { rows } = table.getRowModel();
  const headers = table.getFlatHeaders();
  const columnMetaById = React.useMemo(() => {
    const metaById = {};
    headers.forEach(header => {
      const { column } = header;
      metaById[column.id] = {
        className: getColumnClassName(column),
        style: getCommonPinningStyles(column, columnSizing),
      };
    });
    return metaById;
  }, [headers, columnSizing]);
  // context menu state
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [clickedRow, setClickedRow] = React.useState(null);
  const [clickedCell, setClickedCell] = React.useState(null);

  const handleRightClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const getStringValue = (value) => {
    if (value && typeof value === 'object') {
      return JSON.stringify(value);
    }
    if (value && typeof value === 'string') {
      return value;
    }
    return String(value ?? '');
  };
  const handleCloseMenu = (toCopy) => {
    if (toCopy === "cell" && clickedCell) {
      const value = clickedCell.getValue();
      let valueToCopy = '';

      if (Array.isArray(value)) {
        valueToCopy = value.map(v => getStringValue(v)).join(', ');
      } else {
        valueToCopy = getStringValue(value);
      }

      navigator.clipboard.writeText(valueToCopy);
    }
    if (toCopy === "row" && clickedRow) {
      navigator.clipboard.writeText(JSON.stringify(clickedRow));
    }
    setAnchorEl(null);
  };

  if (isLoading) {
    const pageSize = table.getState().pagination?.pageSize || PAGE_LIMIT;
    const rowCount = Math.max(pageSize, PAGE_LIMIT);
    const skeletonRows = Array.from({ length: rowCount }, (_, i) => i);
    // aria-hidden keeps placeholder cells out of role queries (e.g. "cell"), so nothing can
    // mistake the skeleton for data; the <table> carries aria-busy meanwhile.
    return (
      <tbody aria-hidden="true">
        {skeletonRows.map((rowIndex) => (
          <tr key={`loading-row-${rowIndex}`} className="loading-row">
            {headers.map((header, colIndex) => {
              const columnMeta = columnMetaById[header.column.id];
              return (
                <td key={`loading-td-${header.id}-${rowIndex}`} className={columnMeta?.className} style={columnMeta?.style}>
                  <div className="data-loading" />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    );
  }

  if (rows.length === 0) {
    return (
      <tbody>
        <tr className="empty-row">
          <td colSpan={headers.length}>
            <div className="table-status-container" style={{ width: containerWidth }} data-testid="no-data-available">
              {Strings.noDataAvailable}
            </div>
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <>
      <tbody>
        {rows.map(row => (
          <React.Fragment key={row.id}>
            <tr
              onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              className={`${buttons?.length ? 'with-buttons' : ''} ${getRowClassName?.(row.original) || ''}`}
            >
              {row.getVisibleCells().map((cell) => {
                const { column } = cell;
                const columnMeta = columnMetaById[column.id];
                if (row.original === null) {
                  return (
                    <td data-testid={`column[${cell.column.id}][${row.index}]`}
                      key={`loading-td-${cell.id}`} className={columnMeta?.className} style={columnMeta?.style} >
                      <div className="data-loading" />
                    </td>
                  );
                }
                return (
                  <td
                    data-testid={`column[${cell.column.id}][${row.index}]`}
                    key={cell.id}
                    className={columnMeta?.className}
                    style={columnMeta?.style}
                    onClick={column.columnDef.enableRowClick === false
                      ? event => event.stopPropagation()
                      : undefined}
                    onContextMenu={(e) => {
                      e.preventDefault(); // prevent the default behaviour when right clicked
                      handleRightClick(e, cell);
                      setClickedRow(row.original);
                      setClickedCell(cell);
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                )
              })}
            </tr>
            {shouldEnableRowExpansion && renderExpandedRow && isRowExpanded?.(row.id) && (
              <div className="table-expanded-row">
                <td colSpan={row.getVisibleCells().length}>
                  {renderExpandedRow(row.original)}
                </td>
              </div>
            )}
          </React.Fragment>
        ))}
      </tbody>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
          },
        }}
      >
        <MenuItem onClick={() => handleCloseMenu("cell")}>{Strings.copyCellToClipboard}</MenuItem>
        <MenuItem onClick={() => handleCloseMenu("row")}>{Strings.copyRowToClipboard}</MenuItem>
      </Menu>
    </>
  );
};

const MemoizedTableBody = React.memo(
  TableBody,
  (prev, next) => prev.table.options.data === next.table.options.data
);
